/**
 * Admin Dashboard API Service
 * Handles all API calls for admin dashboard functionality
 */

const API_BASE_URL = '/api';

/**
 * Get authorization headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

/**
 * Handle API response and errors
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    if (response.status === 401) {
      // Token expired, redirect to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('username');
      window.location.href = '/login';
      return;
    }
    
    const errorData = await response.json().catch(() => ({}));
    
    // Handle specific error cases
    if (response.status === 400) {
      throw new Error(errorData.message || 'Invalid request parameters');
    }
    if (response.status === 403) {
      throw new Error('Access denied - can_view_admin_dash permission required');
    }
    
    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Get login statistics for specified number of days
 * @param {number} days - Number of days to look back (1-365)
 */
export const getLoginStats = async (days) => {
  try {
    if (!days || days < 1 || days > 365) {
      throw new Error('Days parameter must be between 1 and 365');
    }

    const response = await fetch(`${API_BASE_URL}/admin/stats/logins?days=${days}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error(`Failed to fetch ${days}-day login stats:`, error);
    throw error;
  }
};

/**
 * Get number of accounts logged in the last 7 days
 */
export const getLogins7Days = async () => {
  return getLoginStats(7);
};

/**
 * Get number of accounts logged in the last 30 days
 */
export const getLogins30Days = async () => {
  return getLoginStats(30);
};

/**
 * Get total number of accounts
 */
export const getTotalAccounts = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/stats/accounts/total`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error('Failed to fetch total accounts:', error);
    throw error;
  }
};

/**
 * Get new account statistics for specified number of days
 * @param {number} days - Number of days to look back (1-365)
 */
export const getNewAccountStats = async (days) => {
  try {
    if (!days || days < 1 || days > 365) {
      throw new Error('Days parameter must be between 1 and 365');
    }

    const response = await fetch(`${API_BASE_URL}/admin/stats/accounts/new?days=${days}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error(`Failed to fetch ${days}-day new account stats:`, error);
    throw error;
  }
};

/**
 * Get number of accounts created in the last 7 days
 */
export const getNewAccounts7Days = async () => {
  return getNewAccountStats(7);
};

/**
 * Get paginated list of all user accounts
 */
export const getAccounts = async (params = {}) => {
  try {
    const {
      page = 1,
      page_size = 25,
      sort_by = 'created_at',
      sort_order = 'desc'
    } = params;
    
    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: page_size.toString(),
      sort_by,
      sort_order
    });
    
    const response = await fetch(`${API_BASE_URL}/admin/accounts?${queryParams}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error('Failed to fetch accounts:', error);
    throw error;
  }
};

/**
 * Format date for display
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'Never';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

/**
 * Format numbers with commas
 */
export const formatNumber = (num) => {
  if (typeof num !== 'number') return '0';
  return num.toLocaleString();
};